import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBooking } from "@/context/BookingContext";
import DashboardMockup from "@/components/landing/DashboardMockup";

export default function Hero() {
  const { open, openWith } = useBooking();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden dmx-hero-glow"
    >
      <div className="absolute inset-0 dmx-grid-bg pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start lg:items-center text-left lg:text-center max-w-4xl lg:mx-auto">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white/70 backdrop-blur text-xs font-medium text-slate-600 dmx-reveal"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#3B82F6] opacity-60 dmx-pulse-dot" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#3B82F6]" />
            </span>
            FreightIQ™ · The modern logistics operating system
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display font-extrabold text-[#0B1F3A] text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.03em] dmx-reveal"
            style={{ animationDelay: "60ms" }}
          >
            Freight Booking
            <br />
            <span className="text-[#0B1F3A]/55">From Turkey.</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-lg md:text-xl text-slate-700 max-w-2xl font-medium dmx-reveal"
            style={{ animationDelay: "120ms" }}
          >
            Book ocean freight with confidence and manage every shipment inside
            one Workspace.
          </p>

          <p
            data-testid="hero-description"
            className="mt-3 text-base md:text-lg text-slate-500 max-w-2xl dmx-reveal"
            style={{ animationDelay: "180ms" }}
          >
            Coordinate freight bookings, monitor shipment milestones and stay
            connected from booking confirmation to final delivery.
          </p>

          <div
            className="mt-9 flex flex-col sm:flex-row gap-3 dmx-reveal"
            style={{ animationDelay: "240ms" }}
          >
            <Button
              data-testid="hero-cta-book"
              onClick={open}
              size="lg"
              className="group rounded-full bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white px-7 h-12 text-base font-semibold shadow-sm hover:shadow-md transition-all -translate-y-0 hover:-translate-y-0.5"
            >
              Book Your Shipment
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button
              data-testid="hero-cta-workspace"
              onClick={() => openWith("workspace")}
              size="lg"
              variant="outline"
              className="rounded-full border-slate-200 bg-white text-[#0B1F3A] hover:bg-slate-50 hover:text-[#0B1F3A] px-7 h-12 text-base font-semibold"
            >
              Create Your Free Workspace
            </Button>
          </div>
        </div>

        <div
          className="relative mt-16 md:mt-24 dmx-reveal"
          style={{ animationDelay: "380ms" }}
        >
          <DashboardMockup />
        </div>
      </div>
    </section>
  );
}
